/**
 * DatSummaryReportPageV2 — ghép danh sách học viên với tổng hợp DAT.
 *
 * Thay cho pipeline Python `dat-report`. Bản cũ chạy được nhưng chỉ chạy được
 * trên đúng một máy: phải cài Python, phải kéo `dat.json` (118MB) từ Atlas về,
 * rồi gõ lệnh. Không ai ngoài người dựng nó chạy lại được, và mỗi kỳ lại phải
 * làm tay từ đầu.
 *
 * Ở đây `dat.json` biến mất hoàn toàn: các phiên trong file đó chính là dữ liệu
 * DAT của hệ thống (đo 2026-09-16: 7.933/7.933 học viên khớp tuyệt đối với bản
 * Python trên cả 7 tổng). Người dùng chỉ còn đưa vào danh sách học viên.
 *
 * Sinh file ở BE chứ không ở trình duyệt, theo đúng cách ReportPageV2 đang làm:
 * số liệu DAT nằm trong Mongo, kéo hàng chục nghìn phiên về máy người dùng chỉ
 * để cộng là vô lý.
 */
import { useRef, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import datV2 from '../../services/datV2';
import V2PageLayout from '../../components/v2/V2PageLayout';

const ACCEPT = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Cột được đọc, theo VỊ TRÍ chứ không theo tiêu đề — hiện ra để người dùng tự
 * đối chiếu trước khi upload, thay vì đợi BE báo lỗi. Bám vị trí là đúng thứ
 * bản Python đang bám; file do phần mềm ngoài xuất, tiêu đề không ổn định.
 */
const COT_DOC = [
  ['B', 'Mã học viên'],
  ['C', 'Tên học viên'],
  ['D', 'Ngày sinh'],
  ['E', 'Giới tính'],
  ['F', 'Số CCCD'],
  ['G', 'Nơi ĐK hộ khẩu'],
  ['H', 'Khóa'],
  ['I', 'Mã khóa'],
];

/** Các cột DAT được ghép thêm vào cuối mỗi dòng. */
const COT_THEM = [
  ['total_distance', 'Tổng quãng đường (km)'],
  ['total_duration', 'Tổng thời gian (giờ)'],
  ['total_night_distance', 'Quãng đường ban đêm (km)'],
  ['total_night_duration', 'Thời gian ban đêm (giờ)'],
  ['total_night_duration_dung_hang', 'Thời gian ban đêm ĐÚNG HẠNG (giờ)'],
  ['total_auto_distance', 'Quãng đường số tự động (km)'],
  ['total_auto_duration', 'Thời gian số tự động (giờ)'],
  ['trainee_name', 'Tên theo dữ liệu DAT'],
  ['ten_khoa_hoc', 'Khóa theo dữ liệu DAT'],
];

export default function DatSummaryReportPageV2() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setResult(null);
    if (f && !/\.xlsx$/i.test(f.name)) {
      toast.error('Chỉ nhận file .xlsx');
      e.target.value = '';
      setFile(null);
      return;
    }
    setFile(f);
  };

  const onExport = async () => {
    if (!file || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await datV2.exportDatSummaryReport(file);

      const url = URL.createObjectURL(res.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Thu hồi sau một nhịp để trình duyệt kịp bắt đầu tải.
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      setResult({
        students: res.studentCount,
        matched: res.matchedCount,
        sessions: res.sessionCount,
      });
      toast.success(`Đã xuất ${res.studentCount} học viên`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Xuất file thất bại.';
      toast.error(msg);
      setResult({ error: msg });
    } finally {
      setBusy(false);
    }
  };

  const onReset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const thieu = result && !result.error ? result.students - result.matched : 0;

  return (
    <V2PageLayout title='Tổng hợp DAT theo danh sách học viên' gridClassName=''>
      <div style={{ maxWidth: 820, overflowY: 'auto' }}>
        <Alert variant='light' className='border small'>
          Đưa vào <strong>file danh sách học viên (.xlsx)</strong>, hệ thống ghép thêm
          số liệu DAT của từng người rồi trả về file <code>hoc-vien-tong-hop-dat.xlsx</code>.
          <div className='mt-2'>
            Không cần file <code>dat.json</code> nữa — số liệu lấy thẳng từ các phiên DAT
            trong hệ thống, nên luôn là số mới nhất chứ không phải số của lần tải gần nhất.
          </div>
        </Alert>

        <Form.Group className='mb-3'>
          <Form.Label className='fw-semibold'>File danh sách học viên (.xlsx)</Form.Label>
          <Form.Control
            ref={inputRef}
            type='file'
            accept={ACCEPT}
            onChange={onPick}
            disabled={busy}
          />
          <Form.Text className='text-muted'>
            Đọc tối đa 4 sheet đầu. Mỗi sheet ra một sheet <code>&lt;tên&gt;_merged</code> trong
            file kết quả. Dòng không có mã học viên bị bỏ qua.
          </Form.Text>
        </Form.Group>

        <div className='d-flex gap-2 mb-3'>
          <Button onClick={onExport} disabled={!file || busy}>
            {busy ? (<><Spinner size='sm' animation='border' className='me-2' />Đang xuất…</>) : 'Xuất file'}
          </Button>
          <Button variant='outline-secondary' onClick={onReset} disabled={busy}>
            Chọn lại
          </Button>
        </div>

        {result && !result.error && (
          <Alert variant={thieu > 0 ? 'warning' : 'success'}>
            <div>
              Đã xuất <strong>{result.students}</strong> học viên, cộng từ{' '}
              <strong>{result.sessions}</strong> phiên DAT.
            </div>
            {thieu > 0 && (
              // Nói ra ngay, vì ô trống trong file kết quả trông y hệt lỗi hệ
              // thống. Phần lớn là học viên chưa có buổi DAT nào, nhưng cũng
              // có thể là mã học viên trong file không khớp hệ thống.
              <div className='mt-1'>
                <strong>{thieu} học viên không có phiên DAT nào</strong> — các cột số liệu của
                họ để trống (không phải số 0). Nếu con số này bất thường, kiểm lại cột B có
                đúng mã học viên theo CSDL không.
              </div>
            )}
          </Alert>
        )}

        {result?.error && <Alert variant='danger'>{result.error}</Alert>}

        <div className='fw-semibold mb-2'>File kết quả gồm những cột nào</div>
        <Table size='sm' bordered className='small'>
          <thead>
            <tr className='text-muted'>
              <th style={{ width: 90 }}>Cột nguồn</th>
              <th>Nội dung</th>
            </tr>
          </thead>
          <tbody>
            {COT_DOC.map(([chu, nhan]) => (
              <tr key={chu}>
                <td><code>{chu}</code></td>
                <td>{nhan}</td>
              </tr>
            ))}
            {COT_THEM.map(([key, nhan]) => (
              <tr key={key}>
                <td className='text-muted'>ghép thêm</td>
                <td>
                  {nhan} <code className='text-muted'>{key}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <div className='text-muted small'>
          Phiên có <strong>vi phạm</strong> bị loại khỏi mọi tổng. Cột{' '}
          <strong>đúng hạng</strong> chỉ cộng giờ đêm khi hạng xe học viên chạy khớp hạng
          của khóa (BTĐ/B1 ↔ B1·B.01·B11, BSS/B2 ↔ B2·B, C1 ↔ C1·C).
        </div>
      </div>
    </V2PageLayout>
  );
}
